import React, { useState } from "react";
import { Alert, Box, Button, LoadingOverlay } from "@mantine/core";
import { IconAlertCircle, IconPlus } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import { SettingsPageLayout, SettingsPageHeader, SettingsTabs } from "./common";
import type { SettingsTabItem } from "./common";
import CommissionRatesTab from "./commissions/CommissionRatesTab";
import CommissionRatesTable from "./commissions/CommissionRatesTable";
import { CommissionDrawerRequest } from "./commissions/CommissionRateDrawer";
import { isServiceCommissionOverridesEnabled } from "../../utils/serviceCommissionOverridesFlag";
import BusinessChargesTab from "./commissions/BusinessChargesTab";
import CommissionsAdvancedSettingsTab from "./commissions/CommissionsAdvancedSettingsTab";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import usePermissions from "../../hooks/usePermissions";

type EditingSection = "advanced" | null;

interface CommissionsManagementProps {
  /** App-level salon-update handler: merges the (partial) PUT response into
   *  context and refreshes /access. Shared with My Business — the Advanced
   *  Settings toggle persists to the same salon record. */
  onSubmit: (response: any, arg2?: boolean, opts?: any) => void;
}

export default function CommissionsManagement({
  onSubmit,
}: CommissionsManagementProps) {
  const { updateSalon } = useApp();
  const { businessInfo, currentUser } = useSalon() as {
    businessInfo: any;
    currentUser: any;
  };
  const perms = usePermissions();
  const commissionsPerms = perms?.settingsCommissions;
  // The Advanced Settings toggle is gated by the Commissions edit permission —
  // it now lives on this page, so this page's permission governs it.
  const readOnly = commissionsPerms?.canEdit === false;

  const [searchParams, setSearchParams] = useSearchParams();
  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const [saving, setSaving] = useState(false);
  // The Commission Rates drawer, opened from a table row or (to add a rate)
  // from the page header's "Add Rate".
  const [ratesDrawer, setRatesDrawer] =
    useState<CommissionDrawerRequest | null>(null);

  // Access: no commissions scope (or, if permissions haven't loaded, not an
  // Owner/Manager) means no access to the page at all.
  const hasAccessByRole =
    currentUser?.role === "Owner" || currentUser?.role === "Manager";
  const hasAccess =
    commissionsPerms != null
      ? commissionsPerms.scope !== "none"
      : hasAccessByRole;

  const tabs: SettingsTabItem[] = [
    { value: "rates", label: "Commission Rates" },
    { value: "business-charges", label: "Business Charges" },
    { value: "advanced", label: "Advanced Settings" },
  ];

  const requestedTab = searchParams.get("tab") || "rates";
  const activeTab = tabs.some((t) => t.value === requestedTab)
    ? requestedTab
    : "rates";

  const ratesRedesign = isServiceCommissionOverridesEnabled();
  const showAddRate =
    hasAccess && ratesRedesign && activeTab === "rates" && !readOnly;

  const handleTabChange = (value: string) => {
    // Leaving a section abandons any in-progress edit in it.
    setEditingSection(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true },
    );
  };

  // One partial PUT /business-locations for whichever section is saving. The app-level
  // handler merges the response (PUT returns only changed fields) and refreshes
  // /access, matching how My Business persists its salon-level settings.
  const handleUpdate = async (payload: Record<string, any> | null) => {
    if (!payload) {
      onSubmit(null);
      setEditingSection(null);
      return;
    }
    try {
      setSaving(true);
      const response = await updateSalon(payload);
      if (response) {
        onSubmit(response, false, { skipSalonPersist: true });
        setEditingSection(null);
      }
    } catch (error) {
      console.error("Error updating commission settings:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageLayout
      header={
        <SettingsPageHeader
          title="Commissions"
          subtitle="Staff commission rates and calculation settings"
          actions={
            showAddRate ? (
              <Button
                color="var(--brand-teal)"
                leftSection={<IconPlus size={16} />}
                onClick={() => setRatesDrawer({ staffId: null, view: "add" })}
              >
                Add Rate
              </Button>
            ) : undefined
          }
        />
      }
    >
      {!hasAccess ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Access Restricted"
          color="yellow"
        >
          Commission settings are only available to Owners and Managers.
        </Alert>
      ) : (
        <>
          <LoadingOverlay visible={!businessInfo} />

          <SettingsTabs
            items={tabs}
            value={activeTab}
            onChange={handleTabChange}
            ariaLabel="Commissions sections"
          />

          <Box>
            {activeTab === "rates" &&
              (ratesRedesign ? (
                <CommissionRatesTable
                  drawerRequest={ratesDrawer}
                  onDrawerRequestChange={setRatesDrawer}
                />
              ) : (
                <CommissionRatesTab />
              ))}

            {activeTab === "business-charges" && <BusinessChargesTab />}

            {activeTab === "advanced" && (
              <CommissionsAdvancedSettingsTab
                businessInfo={businessInfo}
                editing={editingSection === "advanced"}
                saving={saving}
                readOnly={readOnly}
                onEdit={() => setEditingSection("advanced")}
                onCancel={() => setEditingSection(null)}
                onSave={handleUpdate}
              />
            )}
          </Box>
        </>
      )}
    </SettingsPageLayout>
  );
}
